// useBaseEntitlements.js

import { useCallback, useEffect, useState } from 'react';

import {
  clearBaseEntitlementsCache,
  getBaseEntitlements,
} from 'services/bases/BaseEntitlementsService';

/**
 * useBaseEntitlements
 *
 * Loads scaled base entitlements for the active base.
 */
const useBaseEntitlements = ({
  isActive = true,
  baseKey,
}) => {
  const [entitlements, setEntitlements] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    const cleanedBaseKey = String(baseKey || '').trim();
    if (!cleanedBaseKey) {
      setEntitlements(null);
      setError(null);
      setLoading(false);
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      clearBaseEntitlementsCache(cleanedBaseKey);
      const nextEntitlements = await getBaseEntitlements({ baseKey: cleanedBaseKey });
      setEntitlements(nextEntitlements || null);
      return nextEntitlements || null;
    } catch (loadError) {
      setEntitlements(null);
      setError(loadError);
      return null;
    } finally {
      setLoading(false);
    }
  }, [baseKey]);

  useEffect(() => {
    if (!isActive) {
      setEntitlements(null);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let isMounted = true;
    setLoading(true);
    setError(null);

    getBaseEntitlements({ baseKey })
      .then((nextEntitlements) => {
        if (!isMounted) return;
        setEntitlements(nextEntitlements || null);
      })
      .catch((loadError) => {
        if (!isMounted) return;
        setEntitlements(null);
        setError(loadError);
      })
      .finally(() => {
        if (!isMounted) return;
        setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isActive, baseKey]);

  return {
    actorCanUpgrade: Boolean(entitlements?.actor?.canUpgradePlan),
    entitlements,
    error,
    loading,
    reload,
  };
};

export default useBaseEntitlements;
